// @spec IAM-STR-006
// @spec SHL-STR-005
'use client';

import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ConfirmRevokeDialog,
  SessionsList,
  useRevokeAllOtherSessions,
  useRevokeSession,
  useSessions,
} from '@/features/settings/security';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/security/sessions')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.sessions'),
  }),
  component: SessionsPage,
});

/**
 * Session management page.
 *
 * Displays a list of active sessions with the ability to revoke
 * individual sessions or sign out of all other devices.
 */
function SessionsPage() {
  const { t } = useTranslation('settings');
  const { data, isLoading, error, refetch } = useSessions();
  const revokeSession = useRevokeSession();
  const revokeAll = useRevokeAllOtherSessions();

  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const sessions = data?.sessions ?? [];
  const hasOtherSessions = sessions.filter((s) => !s.isCurrent).length > 0;

  const handleRevoke = (sessionId: string) => {
    setRevokingSessionId(sessionId);
    void revokeSession.mutateAsync(sessionId).finally(() => {
      setRevokingSessionId(null);
    });
  };

  const handleRevokeAll = () => {
    void revokeAll.mutateAsync();
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/settings/security" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <header className="flex-1">
            <h1 className="text-3xl font-semibold text-foreground">{t('security.activeSessions')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('security.manageDevices')}
            </p>
          </header>
        </div>

        {error ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sm text-destructive mb-4">
                  {error instanceof Error ? error.message : t('security.failedToLoadSessions')}
                </p>
                <Button variant="outline" onClick={() => void refetch()}>
                  {t('security.tryAgain')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <SessionsLoadingSkeleton />
        ) : (
          <>
            {hasOtherSessions && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div>
                    <CardTitle className="text-base">{t('security.signOutEverywhereElse')}</CardTitle>
                    <CardDescription>{t('security.signOutAllInOneAction')}</CardDescription>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setShowBulkConfirm(true);
                    }}
                    disabled={revokeAll.isPending || revokeSession.isPending}
                  >
                    {revokeAll.isPending ? t('security.signingOut') : t('security.signOutAll')}
                  </Button>
                </CardHeader>
              </Card>
            )}

            <SessionsList
              sessions={sessions}
              onRevoke={handleRevoke}
              isRevoking={revokeSession.isPending || revokeAll.isPending}
              revokingSessionId={revokingSessionId}
            />

            <ConfirmRevokeDialog
              open={showBulkConfirm}
              onOpenChange={setShowBulkConfirm}
              onConfirm={handleRevokeAll}
              isBulk
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Loading skeleton for the sessions page.
 */
function SessionsLoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardContent className="flex items-start gap-4 p-4">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
